import { Box, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React, {
  PropsWithChildren,
  ReactElement,
  useRef,
  useState
} from 'react';

import Android12Switch from '../../../../components/Android12Switch';

export interface CumulativeSelectionProps<TOption> {
  title: string;
  placeholder?: string;
  options: TOption[];
  defaultValue?: TOption[];
  toggleOn?: boolean;
  getOptionLabel: (option: TOption) => string;
  onChange?: (options: TOption[]) => void;
  onToggleChange?: (toggleOn: boolean) => void;
}

const CumulativeSelection = <TOption,>({
  title,
  placeholder,
  options,
  defaultValue = [],
  toggleOn = false,
  getOptionLabel,
  onChange,
  onToggleChange
}: PropsWithChildren<CumulativeSelectionProps<TOption>>): ReactElement => {
  const [value, setValue] = useState<TOption[]>([...(defaultValue || [])]);

  const switchRef = useRef<HTMLInputElement>();
  const switchDefaultChecked = useRef(toggleOn);

  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement> | null,
    checked: boolean
  ) => {
    if (!onToggleChange) return;

    onToggleChange(checked);
  };

  const handleClickInside = () => {
    if (!toggleOn && switchRef.current) {
      switchRef.current.click();
      handleToggleChange(null, true);
    }
  };

  return (
    <Box onClick={handleClickInside}>
      <Box display="flex" alignItems="center">
        {title && <Typography variant="h4">{title}</Typography>}

        <Box flex="1" display="flex" justifyContent="flex-end">
          <Android12Switch
            inputRef={switchRef}
            defaultChecked={switchDefaultChecked.current}
            edge="end"
            color="primary"
            onClick={(event) => event.stopPropagation()}
            onChange={handleToggleChange}
          />
        </Box>
      </Box>

      <Box mt={2}>
        <Autocomplete
          id="legislative-district-filter"
          filterSelectedOptions
          multiple
          disabled={!toggleOn}
          options={options}
          value={value}
          onChange={(_, value) => {
            setValue(value);
            onChange && onChange(value);
          }}
          getOptionLabel={getOptionLabel}
          renderInput={(props) => (
            <TextField
              {...props}
              placeholder={placeholder}
              variant="outlined"
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default CumulativeSelection;
